import { z } from 'zod';
/**
 * User-preference keys. The DB stores one row per `(user_id, key)` pair
 * (EAV). Adding a new preference is additive: add the enum value here, add a
 * new arm to `userPreferenceSchema`, and run a Drizzle migration to ALTER the
 * `user_preference_key` pg_enum.
 */
export var UserPreferenceKey;
(function (UserPreferenceKey) {
    UserPreferenceKey["Theme"] = "theme";
    UserPreferenceKey["AlertsTableColumns"] = "alerts_table_columns";
})(UserPreferenceKey || (UserPreferenceKey = {}));
/**
 * Appearance theme values exposed by the Preferences tab introduced in
 * KIR-2023.
 *
 * - `dark`   — force dark theme regardless of OS preference
 * - `light`  — force light theme regardless of OS preference
 * - `system` — follow the operating system's color scheme (the default)
 */
export var ThemePreference;
(function (ThemePreference) {
    ThemePreference["Dark"] = "dark";
    ThemePreference["Light"] = "light";
    ThemePreference["System"] = "system";
})(ThemePreference || (ThemePreference = {}));
export const userPreferenceKeySchema = z.nativeEnum(UserPreferenceKey);
export const themeValueSchema = z.nativeEnum(ThemePreference);
/** Upper bound on column ids per list — far above any real table width. */
export const MAX_TABLE_COLUMN_IDS = 100;
const columnIdSchema = z.string().min(1).max(100);
const columnIdListSchema = z
    .array(columnIdSchema)
    .max(MAX_TABLE_COLUMN_IDS)
    .refine(ids => new Set(ids).size === ids.length, {
    message: 'column ids must be unique',
});
/** Upper bound on a saved column width in px — far above any real column. */
export const MAX_TABLE_COLUMN_WIDTH = 10000;
const columnWidthsSchema = z
    .record(columnIdSchema, z.number().min(1).max(MAX_TABLE_COLUMN_WIDTH))
    .refine(widths => Object.keys(widths).length <= MAX_TABLE_COLUMN_IDS, {
    message: `at most ${MAX_TABLE_COLUMN_IDS} column widths`,
});
/**
 * Per-user Alerts table column layout (KIR-4089), keyed by column id.
 *
 * - `order`  — the user's column order. Columns the table renders but that are
 *              absent here keep their default position.
 * - `hidden` — columns the user turned off.
 * - `widths` — widths (px) the user dragged, by column id. A column absent
 *              here keeps its default width.
 *
 * Both lists empty and no widths = the table's default layout. Shared by the
 * organization and personal Alerts tables: column ids line up across the two,
 * and an id a table does not render is ignored.
 */
export const tableColumnLayoutSchema = z.object({
    order: columnIdListSchema,
    hidden: columnIdListSchema,
    widths: columnWidthsSchema.optional(),
});
/**
 * Per-key value schema — discriminated union keyed by `key`. Mirrors the
 * `policySettingsUpdateSchema` pattern in `policies.ts`. Each new preference
 * pairs its `UserPreferenceKey` literal with a value schema; Zod statically
 * enforces that callers cannot mix a key with a value that doesn't match.
 */
export const userPreferenceSchema = z.discriminatedUnion('key', [
    z.object({
        key: z.literal(UserPreferenceKey.Theme),
        value: themeValueSchema,
    }),
    z.object({
        key: z.literal(UserPreferenceKey.AlertsTableColumns),
        value: tableColumnLayoutSchema,
    }),
]);
/**
 * Default value for every known preference key. The GET handler merges this
 * registry with the rows it found so the response always contains an entry
 * for every known key — clients never have to special-case "no row yet".
 */
export const DEFAULT_USER_PREFERENCES = {
    [UserPreferenceKey.Theme]: {
        key: UserPreferenceKey.Theme,
        value: ThemePreference.Light,
    },
    [UserPreferenceKey.AlertsTableColumns]: {
        key: UserPreferenceKey.AlertsTableColumns,
        value: { order: [], hidden: [] },
    },
};
const knownPreferenceKeys = new Set(Object.values(UserPreferenceKey));
const entryKey = (item) => typeof item === 'object' && item !== null
    ? item.key
    : undefined;
function withKnownKeysOnly(items) {
    // Non-object entries are kept so the array schema still rejects them.
    const known = items.filter(item => typeof item !== 'object' ||
        item === null ||
        knownPreferenceKeys.has(String(entryKey(item))));
    const present = new Set(known.map(entryKey));
    const missing = Object.values(UserPreferenceKey)
        .filter(key => !present.has(key))
        .map(key => DEFAULT_USER_PREFERENCES[key]);
    return [...known, ...missing];
}
/**
 * Response shape for `GET /api/account/preferences`.
 *
 * One element per known preference key, even when the user has never written
 * a value (the default is substituted server-side). Clients can rely on the
 * array containing exactly one entry per `UserPreferenceKey`.
 *
 * Skew-tolerant in both directions (frontend and backend deploy separately):
 * - entries whose `key` this client doesn't know are dropped, so a backend
 *   that ships a new key (e.g. `alerts_table_columns`, KIR-4089) never breaks
 *   an older bundle still open in a tab;
 * - known keys the response lacks (an older backend) are filled from
 *   `DEFAULT_USER_PREFERENCES`.
 * Known keys that are present are still validated strictly, once each.
 */
export const userPreferencesResponseSchema = z
    .object({
    preferences: z.preprocess(value => (Array.isArray(value) ? withKnownKeysOnly(value) : value), z.array(userPreferenceSchema)),
})
    .refine(({ preferences }) => {
    const expected = Object.values(UserPreferenceKey).length;
    const keys = new Set(preferences.map(p => p.key));
    return (preferences.length === expected && keys.size === preferences.length);
}, {
    message: 'preferences must contain exactly one entry per UserPreferenceKey',
});
/**
 * Body for `PUT /api/account/preferences`.
 *
 * Single `{ key, value }` — the route upserts exactly one row, so two
 * concurrent PUTs against different keys cannot clobber each other. To
 * update more than one preference, call PUT once per key.
 */
export const updateUserPreferenceBodySchema = userPreferenceSchema;
//# sourceMappingURL=user-preferences.js.map